import { useNavigate } from 'react-router-dom';
import customersStorageService from 'src/app/customers/services/customers-storage-service';
import projectsStorageService from 'src/app/projects/services/projects-storage-service';
import config from 'src/config';
import {
    AdminViews,
    EcologyHubViews,
    GNPViews,
    MapViews,
    ProjectViews,
    SurveyViews,
    TableViews,
    HabitatInsightViews,
    LoginViews,
    PortfolioViews,
    SiteNatureRiskViews,
} from 'src/routes/routesConfig';
import { SortOrder } from 'src/shared/types';
import type { SiteExplorerTabs } from 'src/shared/types/siteExplorerTabs';
import authStorageService from './services/auth-storage-service';
import { navRoutesConfig } from '../data/navRoutesConfig';
import { PortfolioSiteExplorerParams } from '../data/navRoutesConfig';

const useAppNavigation = () => {
    const navigate = useNavigate();
    const back = () => navigate(-1);

    const getCurrentCustomerId = () => customersStorageService.getCurrentCustomerId() || '';
    const getCurrentProjectId = () => projectsStorageService.getCurrentProjectId() || '';

    const getPath = (route: keyof typeof navRoutesConfig, params?: any) => {
        const routerBuilder = navRoutesConfig[route];
        if (typeof routerBuilder === 'string') {
            return routerBuilder;
        }
        return routerBuilder(params);
    };

    // Pure path builders, kept alongside their `navigate(...)`-calling counterparts below so a
    // <Link>/<a href> can be rendered with the same destination a click would navigate to.
    const paths = {
        home: () => navRoutesConfig.home,
        projects: (params?: { customerId: string }) =>
            navRoutesConfig.projects({ customerId: params?.customerId || getCurrentCustomerId() }),
        dashboard: (params?: { elementId?: string }) => {
            const path = navRoutesConfig.dashboard({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() });
            const hash = params?.elementId ? `#${params.elementId}` : '';
            return `${path}${hash}`;
        },
        ecologyHub: () => navRoutesConfig.ecologyHub({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() }),

        sampleMetricsTable: () =>
            navRoutesConfig[TableViews.SAMPLE_METRICS]({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() }),
        habitatInsightsMetricsTable: () =>
            navRoutesConfig[TableViews.HABITAT_INSIGHTS_METRICS]({
                projectId: getCurrentProjectId(),
                customerId: getCurrentCustomerId(),
            }),

        speciesMap: (params?: { projectId?: string; customerId?: string }) =>
            navRoutesConfig[MapViews.SPECIES_INSIGHTS_MAP]({
                projectId: params?.projectId || getCurrentProjectId(),
                customerId: params?.customerId || getCurrentCustomerId(),
            }),
        habitatMap: (params?: { projectId?: string; customerId?: string }) =>
            navRoutesConfig[MapViews.HABITAT_INSIGHTS_MAP]({
                projectId: params?.projectId || getCurrentProjectId(),
                customerId: params?.customerId || getCurrentCustomerId(),
            }),

        organisationManagement: (params?: { customerId?: string }) =>
            navRoutesConfig[GNPViews.ORG_MANAGEMENT]({ customerId: params?.customerId ?? getCurrentCustomerId() }),
        userManagement: () => navRoutesConfig[AdminViews.USER_MANAGEMENT]({ customerId: getCurrentCustomerId() }),
        riskPortfolioAdmin: () => navRoutesConfig[AdminViews.RISK_PORTFOLIO]({ customerId: getCurrentCustomerId() }),
        engineApiTokens: () => navRoutesConfig[AdminViews.ENGINE_API_TOKENS] as string,
        estateManagement: () => navRoutesConfig[AdminViews.ESTATE_MANAGEMENT] as string,
        geospatialCatalogue: () => navRoutesConfig[AdminViews.GEOSPATIAL_CATALOGUE] as string,

        speciesTrends: () =>
            navRoutesConfig[EcologyHubViews.SPECIES_TRENDS]({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() }),
        habitatTrends: () =>
            navRoutesConfig[EcologyHubViews.HABITAT_TRENDS]({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() }),
        sampleComparison: () =>
            navRoutesConfig[EcologyHubViews.SAMPLE_COMPARISON]({
                projectId: getCurrentProjectId(),
                customerId: getCurrentCustomerId(),
            }),
        statisticalComparison: () =>
            navRoutesConfig[EcologyHubViews.STATISTICAL_COMPARISON]({
                projectId: getCurrentProjectId(),
                customerId: getCurrentCustomerId(),
            }),
        taxonomicComposition: () =>
            navRoutesConfig[EcologyHubViews.TAXONOMIC_COMPOSITION]({
                projectId: getCurrentProjectId(),
                customerId: getCurrentCustomerId(),
            }),
        communityComparison: () =>
            navRoutesConfig[EcologyHubViews.COMMUNITY_COMPARISON]({
                projectId: getCurrentProjectId(),
                customerId: getCurrentCustomerId(),
            }),

        freemiumSpeciesInsights: () =>
            navRoutesConfig[ProjectViews.FREEMIUM_SPECIES_INSIGHTS]({
                customerId: getCurrentCustomerId(),
                projectId: getCurrentProjectId(),
            }),
        siteFiles: (params?: { filterFileType?: string }) =>
            navRoutesConfig[ProjectViews.SITE_FILES]({
                customerId: getCurrentCustomerId(),
                projectId: getCurrentProjectId(),
                filterFileType: params?.filterFileType,
            }),
        siteSummary: (params?: { projectId?: string; customerId?: string }) =>
            navRoutesConfig[ProjectViews.SITE_SUMMARY]({
                projectId: params?.projectId || getCurrentProjectId(),
                customerId: params?.customerId || getCurrentCustomerId(),
            }),

        surveyDesignSummary: () => navRoutesConfig[SurveyViews.SURVEY_DESIGN_SUMMARY]({ customerId: getCurrentCustomerId() }),

        impersonateUser: () => navRoutesConfig.impersonateUser as string,
        addPasskeyUrl: () => {
            const { cognitoLoginUrl, cognitoClientId } = config;
            const cognitoRedirectUrl = `${window.location.origin}/projects`;
            const staticParams = `client_id=${cognitoClientId}&scope=aws.cognito.signin.user.admin`;
            return `${cognitoLoginUrl}passkeys/add?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
        },

        portfolioSummary: (customerId?: string) =>
            navRoutesConfig[PortfolioViews.PORTFOLIO_SUMMARY]({ customerId: customerId || getCurrentCustomerId() }),
        portfolioSpeciesRiskIndicators: (params?: { tab?: 'iucn-red-list-species' | 'invasive-species'; sortDirection?: SortOrder }) =>
            navRoutesConfig[PortfolioViews.SPECIES_RISK_INDICATORS]({
                customerId: getCurrentCustomerId(),
                tab: params?.tab,
                sortDirection: params?.sortDirection,
            }),
        portfolioSiteExplorer: (params?: PortfolioSiteExplorerParams) =>
            navRoutesConfig[PortfolioViews.PORTFOLIO_SITE_EXPLORER](params || { customerId: getCurrentCustomerId() }),

        globalNatureRiskOverview: () => navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_OVERVIEW]({ customerId: getCurrentCustomerId() }),
        globalNatureRiskStateOfNature: () =>
            navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_STATE_OF_NATURE]({ customerId: getCurrentCustomerId() }),
        globalNatureRiskSiteList: (params: { view?: SiteExplorerTabs; focusSiteId?: string } = {}) =>
            navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_SITE_LIST]({ customerId: getCurrentCustomerId(), ...params }),
        globalNatureRiskImpactsAndDependencies: () =>
            navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_IMPACTS_AND_DEPENDENCIES]({ customerId: getCurrentCustomerId() }),

        siteNatureRiskOverview: (siteId: string) =>
            navRoutesConfig[SiteNatureRiskViews.OVERVIEW]({ siteId, customerId: getCurrentCustomerId() }),
        siteNatureRiskCharacteristics: (siteId: string) =>
            navRoutesConfig[SiteNatureRiskViews.CHARACTERISTICS]({ siteId, customerId: getCurrentCustomerId() }),
        siteNatureRiskStateOfNature: (siteId: string) =>
            navRoutesConfig[SiteNatureRiskViews.STATE_OF_NATURE]({ siteId, customerId: getCurrentCustomerId() }),
        siteNatureRiskImpactsAndDependencies: (siteId: string) =>
            navRoutesConfig[SiteNatureRiskViews.IMPACTS_AND_DEPENDENCIES]({ siteId, customerId: getCurrentCustomerId() }),
    };

    const handlers = {
        toHome: () => navigate(paths.home()),
        toProjects: (params?: { customerId: string }) => navigate(paths.projects(params)),
        toDashboard: (params?: { elementId?: string }) => navigate(paths.dashboard(params)),
        toEcologyHub: () => navigate(paths.ecologyHub()),

        toTermsOfUse: () => navigate(navRoutesConfig.termsOfUse),
        toTermsOfUseDeclined: () => navigate(navRoutesConfig.termsOfUseDeclined),
        toPostAuthRedirect: () => navigate(navRoutesConfig[LoginViews.POST_AUTH_REDIRECT]),
        toImpersonateUser: () => navigate(paths.impersonateUser()),

        toSampleMetricsTable: () => navigate(paths.sampleMetricsTable()),
        toHabitatInsightsMetricsTable: () => navigate(paths.habitatInsightsMetricsTable()),

        toSpeciesMap: (params?: { projectId?: string; customerId?: string }) => navigate(paths.speciesMap(params)),
        toHabitatMap: (params?: { projectId?: string; customerId?: string }) => navigate(paths.habitatMap(params)),

        toOrgManagement: () => navigate(navRoutesConfig[AdminViews.GNP]),
        toUserManagement: () => navigate(paths.userManagement()),
        toRiskPortfolioAdmin: () => navigate(paths.riskPortfolioAdmin()),
        toEngineApiTokens: () => navigate(paths.engineApiTokens()),
        toStacCatalogue: () => navigate(paths.geospatialCatalogue()),

        toSpeciesTrends: () => navigate(paths.speciesTrends()),
        toHabitatTrends: () => navigate(paths.habitatTrends()),
        toSampleComparison: () => navigate(paths.sampleComparison()),
        toStatisticalComparison: () => navigate(paths.statisticalComparison()),
        toTaxonomicComposition: () => navigate(paths.taxonomicComposition()),
        toCommunityComparison: () => navigate(paths.communityComparison()),

        // Projects
        /** The organisation page under Admin > Management, which now administers projects. */
        toOrganisationManagement: (params?: { customerId?: string }) => navigate(paths.organisationManagement(params)),
        toProjectDefinition: () =>
            navigate(
                navRoutesConfig[ProjectViews.PROJECT_DEFINITION]({ customerId: getCurrentCustomerId(), projectId: getCurrentProjectId() })
            ),
        toNewProjectDefinition: () =>
            navigate(navRoutesConfig[ProjectViews.NEW_PROJECT_DEFINITION]({ customerId: getCurrentCustomerId() })),
        toEditProjectDefinition: (params?: { projectId?: string }) =>
            navigate(
                navRoutesConfig[ProjectViews.EDIT_PROJECT_DEFINITION]({
                    customerId: getCurrentCustomerId(),
                    projectId: params?.projectId || getCurrentProjectId(),
                })
            ),
        toFreemiumSpeciesInsights: () => navigate(paths.freemiumSpeciesInsights()),
        toSiteFiles: (params?: { filterFileType?: string }) => navigate(paths.siteFiles(params)),
        toSiteSummary: (params?: { projectId?: string; customerId?: string }) => navigate(paths.siteSummary(params)),

        toHabitatInsightsUpload: ({ projectId = '' } = {}) =>
            navigate(
                navRoutesConfig[HabitatInsightViews.HABITAT_INSIGHTS_UPLOAD]({
                    customerId: getCurrentCustomerId(),
                    projectId,
                })
            ),
        toAddSiteToPlatform: () => navigate(navRoutesConfig[HabitatInsightViews.HABITAT_INSIGHTS_ADD_SITE] as string),
        // Surveys
        toSurveyDesignSummary: () => navigate(paths.surveyDesignSummary()),
        toNewSurveyDesign: () => navigate(navRoutesConfig[SurveyViews.NEW_SURVEY_DESIGN]({ customerId: getCurrentCustomerId() })),
        toSurveyAdmin: () => navigate(navRoutesConfig[SurveyViews.SURVEY_ADMIN]({ customerId: getCurrentCustomerId() })),

        toLogin: () => {
            const { cognitoLoginUrl, cognitoClientId } = config;
            const locationOrigin = window.location.origin;
            const cognitoRedirectUrl = `${locationOrigin}/oauth?provider=aws`;
            const staticParams = `client_id=${cognitoClientId}&response_type=code&scope=aws.cognito.signin.user.admin`;
            const cognitoUrl = `${cognitoLoginUrl}login?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
            authStorageService.clearAll();
            window.location.href = cognitoUrl;
        },
        toUpgradeSubscription: () =>
            navigate(navRoutesConfig.upgradeSubscription({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() })),
        toAddPasskey: () => {
            authStorageService.clearAll();
            window.location.href = paths.addPasskeyUrl();
        },

        toPortfolioSummary: (customerId?: string) => navigate(paths.portfolioSummary(customerId)),

        toPortfolioSpeciesRiskIndicators: (params?: { tab?: 'iucn-red-list-species' | 'invasive-species'; sortDirection?: SortOrder }) =>
            navigate(paths.portfolioSpeciesRiskIndicators(params)),
        toPortfolioSiteExplorer: (params?: PortfolioSiteExplorerParams) => navigate(paths.portfolioSiteExplorer(params)),

        toGlobalNatureRiskOverview: () => navigate(paths.globalNatureRiskOverview()),
        globalNatureRiskOverviewPath: paths.globalNatureRiskOverview(),
        toGlobalNatureRiskStateOfNature: () => navigate(paths.globalNatureRiskStateOfNature()),
        toGlobalNatureRiskSiteList: () => navigate(paths.globalNatureRiskSiteList()),
        toGlobalNatureRiskImpactsAndDependencies: () => navigate(paths.globalNatureRiskImpactsAndDependencies()),
        toGlobalNatureRiskSiteExplorerMap: (focusSiteId?: string) => navigate(paths.globalNatureRiskSiteList({ view: 'map', focusSiteId })),

        toSiteNatureRiskOverview: (siteId: string) => navigate(paths.siteNatureRiskOverview(siteId)),
        toSiteNatureRiskCharacteristics: (siteId: string) => navigate(paths.siteNatureRiskCharacteristics(siteId)),
        toSiteNatureRiskStateOfNature: (siteId: string) => navigate(paths.siteNatureRiskStateOfNature(siteId)),
        toSiteNatureRiskImpactsAndDependencies: (siteId: string) => navigate(paths.siteNatureRiskImpactsAndDependencies(siteId)),
    };

    return {
        to: navigate,
        getPath,
        back,
        paths,
        ...handlers,
    };
};

export default useAppNavigation;
